import {useCallback, useEffect, useMemo, useState} from 'react'
import {useApolloClient, useLazyQuery} from '@apollo/client'
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
  Pagination,
  ProgressBar,
  Spinner,
  Table,
} from 'react-bootstrap'
import {defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {useUploadFile} from '../../../hooks/useUploadFile'
import {useAsyncTaskPolling} from '../../../hooks/useAsyncTaskPolling'
import {
  PREVIEW_CARRIER_IMPORT_STR,
  UPDATE_POLICIES_BY_CARRIER_STR,
  UPDATE_POLICIES_BY_CARRIER_ASYNC_STR,
} from '../../../gql/mutations/policyMutation'
import {
  GET_ALL_CARRIERS_LIST,
  GET_ALL_CARRIERS_WITH_STATUS_LIST,
} from '../../../gql/queries/carriersQuery'
import {
  GET_CARRIER_IMPORT_ASYNC_TASK,
  GET_CARRIER_IMPORT_BATCH_UNMATCHED,
} from '../../../gql/queries/carrierImportQueries'
import {DateInput} from 'app/tfi/components/DateInput'
import {MultiSelectWithActions} from 'app/tfi/components/MultiSelectWithActions'
import {BadgeStatePolicy} from '../../onePolicy/components/BadgeStatePolicy'
import {exportUnmatchedCsv, CarrierImportUnmatchedRow} from './carrierImportExport'
import {parseId} from 'app/tfi/helpers'

// ─────────────────────────────────────────────────────────────────────────────
// Feature flag: set to false to revert to the legacy synchronous upload.
// When false, the app calls the original sync mutation and this file is the
// only thing that needs to change — no backend code is affected.
const USE_ASYNC_CARRIER_IMPORT = true
// ─────────────────────────────────────────────────────────────────────────────

interface CarrierImportSyncResult {
  updatePoliciesByCarrier: {
    success: boolean
    message: string
    processedCount: number
    matchedCount: number
    unmatchedCount: number
    errorsCount: number
    unmatchedRows: CarrierImportUnmatchedRow[]
  }
}

interface CarrierImportAsyncInitResult {
  updatePoliciesByCarrierAsync: {
    success: boolean
    message: string
    task?: {
      taskId: string
      status: string
      progressPercentage?: number | null
      progressMessage?: string | null
    } | null
  }
}

interface MatchingRules {
  mode: 'DEFAULT' | 'CUSTOM'
  matchMemberId: boolean
  matchName: boolean
  matchDob: boolean
  matchZipcode: boolean
  matchState: boolean
  matchPlanType: boolean
  effectiveDateMode: 'IGNORE' | 'EXACT' | 'SAME_MONTH' | 'SAME_YEAR' | 'WITHIN_DAYS'
  effectiveDateToleranceDays: number
  comparePolicyCreated: boolean
  createdDateSource: 'RECEIVED_DATE' | 'ISSUED_DATE' | 'EFFECTIVE_DATE'
  createdDateToleranceDays: number
}

interface CarrierImportPreviewRow {
  rowNumber: number
  outcome: 'MATCHED' | 'UNMATCHED' | 'AMBIGUOUS' | 'ERROR'
  memberId?: string | null
  name?: string | null
  policyId?: string | null
  policyType?: string | null
  method?: string | null
  confidence?: number | null
  reason: string
  carrierEffectiveDate?: string | null
  systemEffectiveDate?: string | null
  candidateCount: number
  candidatesTruncated: boolean
  candidates: CarrierImportPreviewCandidate[]
}

interface CarrierImportPreviewCandidate {
  policyId: string
  policyName?: string | null
  memberId?: string | null
  policyType?: string | null
  carrier?: string | null
  status?: string | null
  effectiveDate?: string | null
  submittedDate?: string | null
  agentName?: string | null
  agentEmail?: string | null
}

interface CarrierImportPreviewResult {
  success: boolean
  message: string
  processedCount: number
  matchedCount: number
  unmatchedCount: number
  ambiguousCount: number
  dateScopeExcludedCount: number
  errorsCount: number
  availableFields: string[]
  unavailableSelectedFields: string[]
  rowsTruncated: boolean
  previewRows: CarrierImportPreviewRow[]
}

interface CarrierImportPreviewResponse {
  previewCarrierImport: CarrierImportPreviewResult
}

interface ImportResult {
  success: boolean
  message: string
  processedCount: number
  matchedCount: number
  unmatchedCount: number
  errorsCount: number
  unmatchedRows: CarrierImportUnmatchedRow[]
}

interface CarrierItem {
  id: string
  name: string
  enabled?: boolean
  aliasIds?: string[]
}

interface PolicyStatusItem {
  code: string
  title: string
  color?: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  carriers: CarrierItem[]
  policyStatuses: PolicyStatusItem[]
  policyTypes: any[]
}

/** Espacio de separación entre nodos JSX; izado a const para no dejar literales sueltos. */
const space = ' '

const M = defineMessages({
  title: {id: 'policies.list.byCarrier.title', defaultMessage: 'Update Policies by Carrier'},
  fieldCarrier: {
    id: 'policies.list.byCarrier.field.carrier',
    defaultMessage: 'File carrier / format',
  },
  fieldTargetCarriers: {
    id: 'policies.list.byCarrier.field.targetCarriers',
    defaultMessage: 'Target carriers',
  },
  targetCarriersHint: {
    id: 'policies.list.byCarrier.hint.targetCarriers',
    defaultMessage: 'Only policies assigned to these carriers can be matched or updated',
  },
  fieldPolicyTypes: {
    id: 'policies.list.byCarrier.field.policyTypes',
    defaultMessage: 'Policy types',
  },
  policyTypesHint: {
    id: 'policies.list.byCarrier.hint.policyTypes',
    defaultMessage: 'Only policies with these types can be matched or updated',
  },
  policyDateFrom: {
    id: 'policies.list.byCarrier.field.searchPolicyDateFrom',
    defaultMessage: 'Search policies from',
  },
  policyDateTo: {
    id: 'policies.list.byCarrier.field.searchPolicyDateTo',
    defaultMessage: 'Search policies through',
  },
  dateCriteria: {
    id: 'policies.list.byCarrier.field.filteredPolicyDate',
    defaultMessage: 'Policy date to filter',
  },
  dateCriteriaEffective: {
    id: 'policies.list.byCarrier.dateCriteria.effective',
    defaultMessage: 'Policies Effective',
  },
  dateCriteriaSubmitted: {
    id: 'policies.list.byCarrier.dateCriteria.submitted',
    defaultMessage: 'Policies Submitted',
  },
  policyDateRangeHint: {
    id: 'policies.list.byCarrier.hint.policyDateRange',
    defaultMessage: 'Only policies within this inclusive date range can be matched or updated',
  },
  limitPolicyDates: {
    id: 'policies.list.byCarrier.dateScope.limit',
    defaultMessage: 'Limit policies by date',
  },
  importScope: {
    id: 'policies.list.byCarrier.scope.title',
    defaultMessage: 'Import scope',
  },
  scopeSummary: {
    id: 'policies.list.byCarrier.scope.summary',
    defaultMessage: '{carriers} selected carriers · {types} selected policy types · {dates}',
  },
  allPolicyDates: {
    id: 'policies.list.byCarrier.scope.allDates',
    defaultMessage: 'all policy dates',
  },
  selectedPolicyDates: {
    id: 'policies.list.byCarrier.scope.selectedDates',
    defaultMessage: '{criteria} dates from {from} through {to}',
  },
  dateFilterDisabledHint: {
    id: 'policies.list.byCarrier.dateScope.disabledHint',
    defaultMessage:
      'Date filtering is off. Matching will search every policy date within the selected carriers and policy types.',
  },
  dateFilterActiveWarning: {
    id: 'policies.list.byCarrier.dateScope.activeWarning',
    defaultMessage:
      'Date filtering is active. Policies outside this range cannot match or be updated.',
  },
  dateScopeExcludedWarning: {
    id: 'policies.list.byCarrier.preview.dateScopeExcluded',
    defaultMessage:
      '{count, plural, one {# likely policy was found but excluded by the date range.} other {# likely policies were found but excluded by the date range.}}',
  },
  searchAllDates: {
    id: 'policies.list.byCarrier.dateScope.searchAllDates',
    defaultMessage: 'Search all dates',
  },
  matchingStrategy: {
    id: 'policies.list.byCarrier.matching.strategy',
    defaultMessage: 'Matching strategy',
  },
  matchingDefault: {
    id: 'policies.list.byCarrier.matching.default',
    defaultMessage: 'Default validation (recommended)',
  },
  matchingCustom: {
    id: 'policies.list.byCarrier.matching.custom',
    defaultMessage: 'Custom validation',
  },
  matchingCustomHint: {
    id: 'policies.list.byCarrier.matching.customHint',
    defaultMessage: 'All selected rules must match and the candidate must be unique',
  },
  matchMemberId: {
    id: 'policies.list.byCarrier.match.memberId',
    defaultMessage: 'Member ID (exact or contained in the policy field)',
  },
  matchName: {id: 'policies.list.byCarrier.match.name', defaultMessage: 'First and last name'},
  matchDob: {id: 'policies.list.byCarrier.match.dob', defaultMessage: 'Date of birth'},
  matchZipcode: {id: 'policies.list.byCarrier.match.zipcode', defaultMessage: 'ZIP code'},
  matchState: {id: 'policies.list.byCarrier.match.state', defaultMessage: 'State'},
  matchPlanType: {
    id: 'policies.list.byCarrier.match.planType',
    defaultMessage: 'Plan / policy type',
  },
  matchEffectiveDate: {
    id: 'policies.list.byCarrier.match.effectiveDate',
    defaultMessage: 'Effective-date comparison',
  },
  effectiveIgnore: {
    id: 'policies.list.byCarrier.match.date.ignore',
    defaultMessage: 'Do not compare',
  },
  effectiveExact: {id: 'policies.list.byCarrier.match.date.exact', defaultMessage: 'Exact date'},
  effectiveSameMonth: {
    id: 'policies.list.byCarrier.match.date.sameMonth',
    defaultMessage: 'Same month and year',
  },
  effectiveSameYear: {
    id: 'policies.list.byCarrier.match.date.sameYear',
    defaultMessage: 'Same year',
  },
  effectiveWithinDays: {
    id: 'policies.list.byCarrier.match.date.withinDays',
    defaultMessage: 'Within a number of days',
  },
  toleranceDays: {
    id: 'policies.list.byCarrier.match.toleranceDays',
    defaultMessage: 'Tolerance (days)',
  },
  compareCreated: {
    id: 'policies.list.byCarrier.match.compareCreated',
    defaultMessage: 'Compare file date with policy submitted date',
  },
  sourceDate: {id: 'policies.list.byCarrier.match.sourceDate', defaultMessage: 'File date'},
  receivedDate: {id: 'policies.list.byCarrier.match.receivedDate', defaultMessage: 'Received date'},
  issuedDate: {id: 'policies.list.byCarrier.match.issuedDate', defaultMessage: 'Issued date'},
  effectiveDate: {
    id: 'policies.list.byCarrier.match.effectiveSource',
    defaultMessage: 'Effective date',
  },
  preview: {id: 'policies.list.byCarrier.preview.button', defaultMessage: 'Preview matches'},
  previewTitle: {id: 'policies.list.byCarrier.preview.title', defaultMessage: 'Import preview'},
  previewNoWrites: {
    id: 'policies.list.byCarrier.preview.noWrites',
    defaultMessage: 'No policies have been updated yet',
  },
  previewAvailable: {
    id: 'policies.list.byCarrier.preview.availableLabel',
    defaultMessage: 'Fields detected:',
  },
  previewUnavailable: {
    id: 'policies.list.byCarrier.preview.unavailable',
    defaultMessage: 'Selected fields not found in the file: {fields}',
  },
  previewTruncated: {
    id: 'policies.list.byCarrier.preview.truncated',
    defaultMessage: 'Showing the first 250 rows',
  },
  previewRequired: {
    id: 'policies.list.byCarrier.preview.required',
    defaultMessage: 'Run a successful preview before applying the import',
  },
  invalidMatchingRules: {
    id: 'policies.list.byCarrier.match.invalid',
    defaultMessage: 'Select Member ID, or Name plus at least one secondary validation',
  },
  applyImport: {id: 'policies.list.byCarrier.applyImport', defaultMessage: 'Apply Import'},
  outcome: {id: 'policies.list.byCarrier.preview.outcome', defaultMessage: 'Outcome'},
  policyType: {id: 'policies.list.byCarrier.preview.policyType', defaultMessage: 'Policy type'},
  viewDetails: {
    id: 'policies.list.byCarrier.preview.viewDetails',
    defaultMessage: 'View details ({count})',
  },
  candidatesTitle: {
    id: 'policies.list.byCarrier.preview.candidates.title',
    defaultMessage: 'Candidate policies',
  },
  matchedPolicyTitle: {
    id: 'policies.list.byCarrier.preview.matchedPolicy.title',
    defaultMessage: 'Matched policy',
  },
  matchedPolicyDescription: {
    id: 'policies.list.byCarrier.preview.matchedPolicy.description',
    defaultMessage: 'This is the policy selected by the matching rules.',
  },
  candidatesDescription: {
    id: 'policies.list.byCarrier.preview.candidates.description',
    defaultMessage:
      '{count} policies satisfy all the selected rules. None will be updated until the match is unique.',
  },
  candidatesTruncated: {
    id: 'policies.list.byCarrier.preview.candidates.truncated',
    defaultMessage: 'Showing the first {shown} of {total} candidate policies.',
  },
  candidatePolicy: {
    id: 'policies.list.byCarrier.preview.candidates.policy',
    defaultMessage: 'Policy',
  },
  candidateCarrier: {
    id: 'policies.list.byCarrier.preview.candidates.carrier',
    defaultMessage: 'Carrier / type',
  },
  candidateEffective: {
    id: 'policies.list.byCarrier.preview.candidates.effective',
    defaultMessage: 'Effective',
  },
  candidateSubmitted: {
    id: 'policies.list.byCarrier.preview.candidates.submitted',
    defaultMessage: 'Submitted',
  },
  candidateAgent: {
    id: 'policies.list.byCarrier.preview.candidates.agent',
    defaultMessage: 'Agent',
  },
  viewPolicy: {
    id: 'policies.list.byCarrier.preview.candidates.viewPolicy',
    defaultMessage: 'View policy',
  },
  loadingCarriers: {
    id: 'policies.list.byCarrier.loadingCarriers',
    defaultMessage: 'Loading carriers...',
  },
  selectCarrier: {id: 'policies.list.byCarrier.selectCarrier', defaultMessage: 'Select carrier...'},
  supportedCarriers: {
    id: 'policies.list.byCarrier.supportedCarriers',
    defaultMessage:
      'Supported carriers: Ambetter, Oscar, BCBS, Blue Shield of California, Molina, Anthem, Imperial, Sentara, Highmark, Medica, Select Health, United Healthcare, United Health One - Supplemental',
  },
  asOfDate: {id: 'policies.list.byCarrier.asOfDate', defaultMessage: 'As-of Date'},
  asOfDateHint: {
    id: 'policies.list.byCarrier.asOfDateHint',
    defaultMessage: 'The date through which the data in this carrier file is current',
  },
  fileCsv: {id: 'policies.list.byCarrier.fileCsv', defaultMessage: 'File (CSV)'},
  loadingUnmatched: {
    id: 'policies.list.byCarrier.loadingUnmatched',
    defaultMessage: 'Loading unmatched rows...',
  },
  uploadFailed: {id: 'policies.list.byCarrier.uploadFailed', defaultMessage: 'Upload failed'},
  statProcessed: {id: 'policies.list.byCarrier.stat.processed', defaultMessage: 'Processed:'},
  statMatched: {id: 'policies.list.byCarrier.stat.matched', defaultMessage: 'Matched:'},
  statUnmatched: {id: 'policies.list.byCarrier.stat.unmatched', defaultMessage: 'Unmatched:'},
  statAmbiguous: {id: 'policies.list.byCarrier.stat.ambiguous', defaultMessage: 'Ambiguous:'},
  statErrors: {id: 'policies.list.byCarrier.stat.errors', defaultMessage: 'Errors:'},
  unmatchedRows: {
    id: 'policies.list.byCarrier.unmatchedRows',
    defaultMessage: 'Unmatched Rows ({count})',
  },
  ofTotal: {id: 'policies.list.byCarrier.ofTotal', defaultMessage: '{shown} of {total}'},
  exportCsv: {id: 'policies.list.byCarrier.exportCsv', defaultMessage: 'Export Unmatched CSV'},
  phSearch: {
    id: 'policies.list.byCarrier.phSearch',
    defaultMessage: 'Search by Member ID or Name...',
  },
  thRow: {id: 'policies.list.byCarrier.th.row', defaultMessage: 'Row'},
  thMemberId: {id: 'policies.list.byCarrier.th.memberId', defaultMessage: 'Member ID'},
  thName: {id: 'policies.list.byCarrier.th.name', defaultMessage: 'Name'},
  thCarrierStatus: {
    id: 'policies.list.byCarrier.th.carrierStatus',
    defaultMessage: 'Carrier Status',
  },
  thSystemStatus: {id: 'policies.list.byCarrier.th.systemStatus', defaultMessage: 'System Status'},
  thAgent: {id: 'policies.list.byCarrier.th.agent', defaultMessage: 'Agent'},
  thReason: {id: 'policies.list.byCarrier.th.reason', defaultMessage: 'Reason'},
  pageOf: {id: 'policies.list.byCarrier.pageOf', defaultMessage: 'Page {page} of {total}'},
  btnUploading: {id: 'policies.list.byCarrier.btn.uploading', defaultMessage: 'Uploading...'},
  processing: {id: 'policies.list.byCarrier.processing', defaultMessage: 'Processing...'},
  btnWorking: {id: 'policies.list.byCarrier.btn.working', defaultMessage: 'Working...'},
  btnUploadProcess: {
    id: 'policies.list.byCarrier.btn.uploadProcess',
    defaultMessage: 'Upload & Process',
  },
  toastFillFields: {
    id: 'policies.list.byCarrier.toast.fillFields',
    defaultMessage: 'Please fill all required fields',
  },
  toastInvalidDateRange: {
    id: 'policies.list.byCarrier.toast.invalidDateRange',
    defaultMessage: 'The policy start date cannot be after the end date',
  },
  toastFailedStart: {
    id: 'policies.list.byCarrier.toast.failedStart',
    defaultMessage: 'Failed to start import task',
  },
  toastImportCompleted: {
    id: 'policies.list.byCarrier.toast.importCompleted',
    defaultMessage: 'Import completed successfully',
  },
  msgImportCompleted: {
    id: 'policies.list.byCarrier.msg.importCompleted',
    defaultMessage: 'Import completed',
  },
  importFailed: {
    id: 'policies.list.byCarrier.importFailed',
    defaultMessage: 'Import failed: {message}',
  },
  progTaskQueued: {id: 'policies.list.byCarrier.prog.taskQueued', defaultMessage: 'Task queued...'},
  close: {id: 'common.action.close', defaultMessage: 'Close'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
})

const SUPPORTED_CARRIERS = [
  'ambetter',
  'oscar',
  'blue cross',
  'blue shield',
  'bcbs',
  'molina',
  'anthem',
  'imperial',
  'sentara',
  'jefferson',
  'independence',
  'amerihealth',
  'ameri health',
  'cigna',
  'wellpoint',
  'alliant',
  'highmark',
  'medica',
  'select health',
  'selecthealth',
]
const ANTHEM_CARRIER_FALLBACK: CarrierItem = {id: '17', name: 'Anthem', enabled: false}
const PAGE_SIZE = 25

const DEFAULT_MATCHING_RULES: MatchingRules = {
  mode: 'DEFAULT',
  matchMemberId: true,
  matchName: false,
  matchDob: false,
  matchZipcode: false,
  matchState: false,
  matchPlanType: false,
  effectiveDateMode: 'IGNORE',
  effectiveDateToleranceDays: 15,
  comparePolicyCreated: false,
  createdDateSource: 'RECEIVED_DATE',
  createdDateToleranceDays: 30,
}

const getDefaultPolicyDateRange = () => {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return {from: `${year}-01-01`, to: `${year}-${month}-${day}`}
}

export const ModalUpdatePoliciesByCarrier: React.FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
  carriers,
  policyStatuses,
  policyTypes,
}) => {
  const intl = useIntl()
  const dash = '-' // non-translatable empty-cell placeholder (hoisted)
  const percentSign = '%' // non-translatable symbol (hoisted)
  const [carrierId, setCarrierId] = useState('')
  const [targetCarrierIds, setTargetCarrierIds] = useState<string[]>([])
  const [policyTypeIds, setPolicyTypeIds] = useState<string[]>([])
  const [limitPolicyDates, setLimitPolicyDates] = useState(false)
  const [policyDateFrom, setPolicyDateFrom] = useState<string>(
    () => getDefaultPolicyDateRange().from
  )
  const [policyDateTo, setPolicyDateTo] = useState<string>(() => getDefaultPolicyDateRange().to)
  const [dateCriteria, setDateCriteria] = useState<'submitted' | 'effective'>('submitted')
  const [asOfDate, setAsOfDate] = useState<string>('')
  const [file, setFile] = useState<File | null>(null)
  const [matchingRules, setMatchingRules] = useState<MatchingRules>(DEFAULT_MATCHING_RULES)
  const [previewResult, setPreviewResult] = useState<CarrierImportPreviewResult | null>(null)
  const [matchDetail, setMatchDetail] = useState<CarrierImportPreviewRow | null>(null)
  const [detectedFields, setDetectedFields] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [fetchedCarriers, setFetchedCarriers] = useState<CarrierItem[]>([])
  const [catalogCarriers, setCatalogCarriers] = useState<CarrierItem[]>([])
  const [loadingCarriers, setLoadingCarriers] = useState(false)

  // Unified result state — populated by either sync or async path
  const [importResult, setImportResult] = useState<ImportResult | null>(null)

  // Async-specific state
  const [asyncTaskId, setAsyncTaskId] = useState<string | null>(null)
  const [asyncProgress, setAsyncProgress] = useState<{pct: number; msg: string} | null>(null)
  const [fetchingUnmatched, setFetchingUnmatched] = useState(false)

  const [uploadFn, {loading: isSyncUploading, error: syncError}] =
    useUploadFile<CarrierImportSyncResult>()
  const [uploadAsyncFn, {loading: isAsyncUploading}] = useUploadFile<CarrierImportAsyncInitResult>()
  const [previewUploadFn, {loading: isPreviewing}] = useUploadFile<CarrierImportPreviewResponse>()

  const [fetchUnmatched] = useLazyQuery(GET_CARRIER_IMPORT_BATCH_UNMATCHED, {
    fetchPolicy: 'network-only',
  })

  const client = useApolloClient()

  // Polling hook — only active when asyncTaskId is set
  const {task: pollingTask, isPolling} = useAsyncTaskPolling({
    taskId: asyncTaskId,
    query: GET_CARRIER_IMPORT_ASYNC_TASK,
    dataKey: 'carrierImportAsyncTask',
    pollingInterval: 2000,
    onSuccess: async (resultStr) => {
      setAsyncTaskId(null)
      setAsyncProgress(null)
      try {
        const parsed = JSON.parse(resultStr)
        // Fetch unmatched rows from the DB using the batch_id from the task result
        let unmatchedRows: CarrierImportUnmatchedRow[] = []
        if (parsed.batch_id) {
          setFetchingUnmatched(true)
          try {
            const res = await fetchUnmatched({variables: {batchId: parsed.batch_id}})
            unmatchedRows = res.data?.carrierImportBatchUnmatched ?? []
          } catch {
            // Non-critical: show counts even if unmatched detail fails
          } finally {
            setFetchingUnmatched(false)
          }
        }
        setImportResult({
          success: parsed.success ?? true,
          message: parsed.message ?? intl.formatMessage(M.msgImportCompleted),
          processedCount: parsed.processed_count ?? 0,
          matchedCount: parsed.matched_count ?? 0,
          unmatchedCount: parsed.unmatched_count ?? 0,
          errorsCount: parsed.errors_count ?? 0,
          unmatchedRows,
        })
      } catch {
        toast.success(intl.formatMessage(M.toastImportCompleted))
      }
    },
    onError: (errMsg) => {
      setAsyncTaskId(null)
      setAsyncProgress(null)
      toast.error(intl.formatMessage(M.importFailed, {message: errMsg}))
    },
  })

  // Keep progress bar in sync with polling task
  useEffect(() => {
    if (pollingTask) {
      setAsyncProgress({
        pct: pollingTask.progressPercentage ?? 0,
        msg: pollingTask.progressMessage ?? intl.formatMessage(M.processing),
      })
    }
  }, [pollingTask])

  useEffect(() => {
    if (!isOpen) return

    setLoadingCarriers(true)
    const searchTerms = [
      'ambetter',
      'oscar',
      'blue cross',
      'blue shield',
      'bcbs',
      'molina',
      'anthem',
      'imperial',
      'sentara',
      'jefferson',
      'independence',
      'amerihealth',
      'cigna',
      'wellpoint',
      'alliant',
      'highmark',
      'medica',
      'select health',
      'selecthealth',
      'united',
    ]

    const supportedCarrierRequests = searchTerms.map((name) =>
      client.query({
        query: GET_ALL_CARRIERS_WITH_STATUS_LIST,
        variables: {status: 'all', name, first: 20},
        fetchPolicy: 'network-only',
      })
    )

    Promise.all([
      Promise.all(supportedCarrierRequests),
      client.query({query: GET_ALL_CARRIERS_LIST, fetchPolicy: 'network-only'}),
    ])
      .then(([results, catalogResult]) => {
        const seen = new Set<string>()
        const merged: CarrierItem[] = []
        for (const result of results) {
          const edges = result.data?.allInsuranceOperators?.edges || []
          for (const edge of edges) {
            const node = edge?.node
            if (node && !seen.has(String(node.id))) {
              seen.add(String(node.id))
              merged.push(node)
            }
          }
        }
        setFetchedCarriers(merged)
        setCatalogCarriers(catalogResult.data?.allInsuranceOperatorsList || [])
      })
      .catch(() => {})
      .finally(() => setLoadingCarriers(false))
  }, [isOpen, client])

  const allCarriers: CarrierItem[] = useMemo(() => {
    const seenIds = new Set<string>()
    const byName = new Map<string, CarrierItem>()
    const base: CarrierItem[] = []
    for (const carrier of [...(carriers || []), ...catalogCarriers, ...fetchedCarriers]) {
      const id = String(parseId(carrier?.id || ''))
      if (!id || id === '0' || seenIds.has(id)) continue
      seenIds.add(id)

      const nameKey = (carrier?.name || '').trim().toLowerCase().replace(/\s+/g, ' ') || id
      const existing = byName.get(nameKey)
      if (existing) {
        existing.aliasIds = [...(existing.aliasIds || [existing.id]), id]
        continue
      }

      const normalizedCarrier = {...carrier, id, aliasIds: [id]}
      byName.set(nameKey, normalizedCarrier)
      base.push(normalizedCarrier)
    }
    const hasAnthem = base.some((carrier) => {
      const name = carrier?.name?.trim().toLowerCase() || ''
      return String(carrier?.id) === ANTHEM_CARRIER_FALLBACK.id || name === 'anthem'
    })
    if (hasAnthem) return base
    return [...base, ANTHEM_CARRIER_FALLBACK]
  }, [carriers, catalogCarriers, fetchedCarriers])

  const supportedCarriers = useMemo(
    () =>
      allCarriers
        .filter((carrier) => {
          const name = carrier?.name?.toLowerCase() || ''
          if (name.includes('capital blue cross')) return false
          if (name.includes('cigna') && name !== 'cigna') return false
          if (
            (name.includes('amerihealth') || name.includes('ameri health')) &&
            name.includes('caritas')
          )
            return false
          if (name === 'united healthcare' || name === 'united health one - supplemental')
            return true
          if (name === 'cigna') return true
          if (name === 'medica') return true
          return SUPPORTED_CARRIERS.some((sc) => name.includes(sc))
        })
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || '')),
    [allCarriers]
  )

  const targetCarrierOptions = useMemo(
    () =>
      allCarriers
        .map((carrier) => ({
          value: String(carrier.id),
          label: carrier.name,
          carrierIds: carrier.aliasIds || [String(carrier.id)],
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [allCarriers]
  )

  const policyTypeOptions = useMemo(
    () =>
      (policyTypes || []).map((category) => ({
        label: category.name,
        options: (category.policyTypeSet?.edges || []).map(({node}: any) => ({
          value: String(parseId(node.id)),
          label: node.typePolicy,
        })),
      })),
    [policyTypes]
  )

  const selectedTargetCarriers = useMemo(
    () =>
      targetCarrierOptions.filter((option) =>
        option.carrierIds.some((id) => targetCarrierIds.includes(id))
      ),
    [targetCarrierIds, targetCarrierOptions]
  )

  const selectedPolicyTypes = useMemo(() => {
    const options = policyTypeOptions.flatMap((category) => category.options)
    return options.filter((option) => policyTypeIds.includes(option.value))
  }, [policyTypeIds, policyTypeOptions])

  const statusTitleByCode = useMemo(() => {
    const map: Record<string, string> = {}
    for (const status of policyStatuses || []) {
      if (status?.code) map[status.code] = status.title || ''
    }
    return map
  }, [policyStatuses])

  const statusColorByCode = useMemo(() => {
    const map: Record<string, string> = {}
    for (const status of policyStatuses || []) {
      if (status?.code) map[status.code] = status.color || 'secondary'
    }
    return map
  }, [policyStatuses])

  const customRulesValid = useMemo(() => {
    if (matchingRules.mode === 'DEFAULT') return true
    const hasSecondary =
      matchingRules.matchDob ||
      matchingRules.matchZipcode ||
      matchingRules.matchState ||
      matchingRules.matchPlanType ||
      matchingRules.effectiveDateMode !== 'IGNORE' ||
      matchingRules.comparePolicyCreated
    return matchingRules.matchMemberId || (matchingRules.matchName && hasSecondary)
  }, [matchingRules])

  const dateScopeLabel = useMemo(() => {
    if (!limitPolicyDates) return intl.formatMessage(M.allPolicyDates)
    return intl.formatMessage(M.selectedPolicyDates, {
      criteria:
        dateCriteria === 'submitted'
          ? intl.formatMessage(M.dateCriteriaSubmitted)
          : intl.formatMessage(M.dateCriteriaEffective),
      from: policyDateFrom,
      to: policyDateTo,
    })
  }, [limitPolicyDates, dateCriteria, policyDateFrom, policyDateTo, intl])

  useEffect(() => {
    setPreviewResult(null)
    setMatchDetail(null)
  }, [
    carrierId,
    targetCarrierIds,
    policyTypeIds,
    limitPolicyDates,
    policyDateFrom,
    policyDateTo,
    dateCriteria,
    asOfDate,
    file,
    matchingRules,
  ])

  useEffect(() => {
    setDetectedFields([])
  }, [file])

  const buildFormData = useCallback(
    (mutationStr: string) => {
      if (
        !carrierId ||
        targetCarrierIds.length === 0 ||
        policyTypeIds.length === 0 ||
        (limitPolicyDates && (!policyDateFrom || !policyDateTo)) ||
        !asOfDate ||
        !file
      )
        return null
      const formData = new FormData()
      formData.append('0', file, file.name)
      formData.append(
        'operations',
        JSON.stringify({
          query: mutationStr,
          variables: {
            carrierId: parseId(carrierId),
            targetCarrierIds: targetCarrierIds.map((id) => parseId(id)),
            policyTypeIds: policyTypeIds.map((id) => parseId(id)),
            policyDateFrom: limitPolicyDates ? policyDateFrom : null,
            policyDateTo: limitPolicyDates ? policyDateTo : null,
            dateCriteria,
            asOfDate,
            matchingRules,
            file: null,
          },
        })
      )
      formData.append('map', JSON.stringify({'0': ['variables.file']}))
      return formData
    },
    [
      carrierId,
      targetCarrierIds,
      policyTypeIds,
      limitPolicyDates,
      policyDateFrom,
      policyDateTo,
      dateCriteria,
      asOfDate,
      matchingRules,
      file,
    ]
  )

  const validateImportForm = useCallback(() => {
    if (
      !carrierId ||
      targetCarrierIds.length === 0 ||
      policyTypeIds.length === 0 ||
      (limitPolicyDates && (!policyDateFrom || !policyDateTo)) ||
      !asOfDate ||
      !file
    ) {
      toast.error(intl.formatMessage(M.toastFillFields))
      return false
    }
    if (limitPolicyDates && policyDateFrom > policyDateTo) {
      toast.error(intl.formatMessage(M.toastInvalidDateRange))
      return false
    }
    if (!customRulesValid) {
      toast.error(intl.formatMessage(M.invalidMatchingRules))
      return false
    }
    return true
  }, [
    carrierId,
    targetCarrierIds,
    policyTypeIds,
    limitPolicyDates,
    policyDateFrom,
    policyDateTo,
    asOfDate,
    file,
    customRulesValid,
    intl,
  ])

  const handlePreview = useCallback(async () => {
    if (!validateImportForm()) return
    const formData = buildFormData(PREVIEW_CARRIER_IMPORT_STR)
    if (!formData) return
    try {
      const result = await previewUploadFn(formData)
      const payload = result?.previewCarrierImport
      if (!payload?.success) {
        toast.error(payload?.message || intl.formatMessage(M.uploadFailed))
        return
      }
      setPreviewResult(payload)
      setDetectedFields(payload.availableFields || [])
    } catch {
      // error handled by useUploadFile
    }
  }, [validateImportForm, buildFormData, previewUploadFn, intl])

  const handleUpload = useCallback(async () => {
    if (!validateImportForm()) return
    if (!previewResult?.success || previewResult.unavailableSelectedFields.length > 0) {
      toast.error(intl.formatMessage(M.previewRequired))
      return
    }

    if (USE_ASYNC_CARRIER_IMPORT) {
      // ── Async path ──────────────────────────────────────────────────────────
      const formData = buildFormData(UPDATE_POLICIES_BY_CARRIER_ASYNC_STR)
      if (!formData) return
      try {
        const result = await uploadAsyncFn(formData)
        const payload = result?.updatePoliciesByCarrierAsync
        if (!payload?.success || !payload?.task?.taskId) {
          toast.error(payload?.message || intl.formatMessage(M.toastFailedStart))
          return
        }
        setAsyncProgress({pct: 0, msg: intl.formatMessage(M.progTaskQueued)})
        setAsyncTaskId(payload.task.taskId)
      } catch {
        // error already set by useUploadFile
      }
    } else {
      // ── Sync path (legacy — untouched) ──────────────────────────────────────
      const formData = buildFormData(UPDATE_POLICIES_BY_CARRIER_STR)
      if (!formData) return
      try {
        const result = await uploadFn(formData)
        const payload = result?.updatePoliciesByCarrier
        if (payload) {
          setImportResult({
            success: payload.success,
            message: payload.message,
            processedCount: payload.processedCount,
            matchedCount: payload.matchedCount,
            unmatchedCount: payload.unmatchedCount,
            errorsCount: payload.errorsCount,
            unmatchedRows: payload.unmatchedRows ?? [],
          })
        }
      } catch {
        // error handled by useUploadFile
      }
    }
  }, [
    carrierId,
    targetCarrierIds,
    policyTypeIds,
    limitPolicyDates,
    policyDateFrom,
    policyDateTo,
    asOfDate,
    file,
    buildFormData,
    uploadFn,
    uploadAsyncFn,
    validateImportForm,
    previewResult,
  ])

  const handleClose = () => {
    setCarrierId('')
    setTargetCarrierIds([])
    setPolicyTypeIds([])
    setLimitPolicyDates(false)
    const defaultDateRange = getDefaultPolicyDateRange()
    setPolicyDateFrom(defaultDateRange.from)
    setPolicyDateTo(defaultDateRange.to)
    setDateCriteria('submitted')
    setAsOfDate('')
    setFile(null)
    setMatchingRules(DEFAULT_MATCHING_RULES)
    setPreviewResult(null)
    setMatchDetail(null)
    setDetectedFields([])
    setSearchQuery('')
    setCurrentPage(1)
    setAsyncTaskId(null)
    setAsyncProgress(null)
    if (importResult?.success) {
      onSuccess()
    }
    setImportResult(null)
    onClose()
  }

  // Filter & paginate unmatched rows
  const filteredRows = useMemo(() => {
    const rows = importResult?.unmatchedRows ?? []
    if (!searchQuery.trim()) return rows
    const q = searchQuery.trim().toLowerCase()
    return rows.filter(
      (row) =>
        (row.memberId && row.memberId.toLowerCase().includes(q)) ||
        (row.name && row.name.toLowerCase().includes(q))
    )
  }, [importResult?.unmatchedRows, searchQuery])

  const totalFiltered = filteredRows.length
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE))
  const safePage = Math.min(currentPage, totalPages)
  const paginatedRows = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }

  const paginationItems = useMemo(() => {
    const items: JSX.Element[] = []
    const maxVisible = 5
    let start = Math.max(1, safePage - Math.floor(maxVisible / 2))
    const end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1)
    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item key={p} active={p === safePage} onClick={() => setCurrentPage(p)}>
          {p}
        </Pagination.Item>
      )
    }
    return items
  }, [safePage, totalPages])

  const isWorking =
    isSyncUploading || isAsyncUploading || isPreviewing || isPolling || fetchingUnmatched

  return (
    <>
      <Modal show={isOpen} onHide={handleClose} size='xl' backdrop='static'>
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage(M.title)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!importResult ? (
            <>
              <Form.Group className='mb-3'>
                <Form.Label>{intl.formatMessage(M.fieldCarrier)}</Form.Label>
                <Form.Select
                  value={carrierId}
                  onChange={(e) => {
                    const value = e.target.value
                    setCarrierId(value)
                    const targetOption = targetCarrierOptions.find(
                      (option) => option.value === value
                    )
                    setTargetCarrierIds(value ? targetOption?.carrierIds || [value] : [])
                  }}
                  disabled={isWorking || loadingCarriers}
                >
                  <option value=''>
                    {loadingCarriers
                      ? intl.formatMessage(M.loadingCarriers)
                      : intl.formatMessage(M.selectCarrier)}
                  </option>
                  {supportedCarriers.map((carrier) => (
                    <option key={carrier.id} value={carrier.id}>
                      {carrier.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className='text-muted'>
                  {intl.formatMessage(M.supportedCarriers)}
                </Form.Text>
              </Form.Group>

              <Form.Group className='mb-3'>
                <Form.Label>{intl.formatMessage(M.fieldTargetCarriers)}</Form.Label>
                <MultiSelectWithActions
                  value={selectedTargetCarriers}
                  options={targetCarrierOptions}
                  onChange={(selected: Array<{value: string; carrierIds: string[]}>) =>
                    setTargetCarrierIds(
                      Array.from(
                        new Set(
                          (selected || []).flatMap(
                            (option) => option.carrierIds || [String(option.value)]
                          )
                        )
                      )
                    )
                  }
                  isDisabled={isWorking}
                />
                <Form.Text className='text-muted'>
                  {intl.formatMessage(M.targetCarriersHint)}
                </Form.Text>
              </Form.Group>

              <Form.Group className='mb-3'>
                <Form.Label>{intl.formatMessage(M.fieldPolicyTypes)}</Form.Label>
                <MultiSelectWithActions
                  value={selectedPolicyTypes}
                  options={policyTypeOptions}
                  onChange={(selected: Array<{value: string}>) =>
                    setPolicyTypeIds((selected || []).map((option) => String(option.value)))
                  }
                  isDisabled={isWorking}
                />
                <Form.Text className='text-muted'>
                  {intl.formatMessage(M.policyTypesHint)}
                </Form.Text>
              </Form.Group>

              <div className='border rounded p-3 mb-3 bg-light'>
                <Form.Label className='fw-bold d-block'>
                  {intl.formatMessage(M.matchingStrategy)}
                </Form.Label>
                <div className='d-flex flex-wrap gap-4 mb-2'>
                  <Form.Check
                    type='radio'
                    name='matchingMode'
                    id='matchingModeDefault'
                    label={intl.formatMessage(M.matchingDefault)}
                    checked={matchingRules.mode === 'DEFAULT'}
                    disabled={isWorking}
                    onChange={() => setMatchingRules((rules) => ({...rules, mode: 'DEFAULT'}))}
                  />
                  <Form.Check
                    type='radio'
                    name='matchingMode'
                    id='matchingModeCustom'
                    label={intl.formatMessage(M.matchingCustom)}
                    checked={matchingRules.mode === 'CUSTOM'}
                    disabled={isWorking}
                    onChange={() => setMatchingRules((rules) => ({...rules, mode: 'CUSTOM'}))}
                  />
                </div>

                {matchingRules.mode === 'CUSTOM' && (
                  <>
                    <Form.Text className='text-muted d-block mb-3'>
                      {intl.formatMessage(M.matchingCustomHint)}
                    </Form.Text>
                    <div className='row g-2 mb-3'>
                      {[
                        ['matchMemberId', M.matchMemberId],
                        ['matchName', M.matchName],
                        ['matchDob', M.matchDob],
                        ['matchZipcode', M.matchZipcode],
                        ['matchState', M.matchState],
                        ['matchPlanType', M.matchPlanType],
                      ].map(([field, message]) => (
                        <div className='col-sm-6 col-lg-4' key={String(field)}>
                          <Form.Check
                            id={`carrierImport-${field}`}
                            label={intl.formatMessage(message as any)}
                            checked={Boolean(matchingRules[field as keyof MatchingRules])}
                            disabled={isWorking}
                            onChange={(event) =>
                              setMatchingRules((rules) => ({
                                ...rules,
                                [field as keyof MatchingRules]: event.target.checked,
                              }))
                            }
                          />
                        </div>
                      ))}
                    </div>

                    <div className='row g-3 mb-3'>
                      <div className='col-md-8'>
                        <Form.Group>
                          <Form.Label>{intl.formatMessage(M.matchEffectiveDate)}</Form.Label>
                          <Form.Select
                            value={matchingRules.effectiveDateMode}
                            disabled={isWorking}
                            onChange={(event) =>
                              setMatchingRules((rules) => ({
                                ...rules,
                                effectiveDateMode: event.target
                                  .value as MatchingRules['effectiveDateMode'],
                              }))
                            }
                          >
                            <option value='IGNORE'>{intl.formatMessage(M.effectiveIgnore)}</option>
                            <option value='EXACT'>{intl.formatMessage(M.effectiveExact)}</option>
                            <option value='SAME_MONTH'>
                              {intl.formatMessage(M.effectiveSameMonth)}
                            </option>
                            <option value='SAME_YEAR'>
                              {intl.formatMessage(M.effectiveSameYear)}
                            </option>
                            <option value='WITHIN_DAYS'>
                              {intl.formatMessage(M.effectiveWithinDays)}
                            </option>
                          </Form.Select>
                        </Form.Group>
                      </div>
                      {matchingRules.effectiveDateMode === 'WITHIN_DAYS' && (
                        <div className='col-md-4'>
                          <Form.Group>
                            <Form.Label>{intl.formatMessage(M.toleranceDays)}</Form.Label>
                            <Form.Control
                              type='number'
                              min={0}
                              max={366}
                              value={matchingRules.effectiveDateToleranceDays}
                              disabled={isWorking}
                              onChange={(event) =>
                                setMatchingRules((rules) => ({
                                  ...rules,
                                  effectiveDateToleranceDays: Number(event.target.value),
                                }))
                              }
                            />
                          </Form.Group>
                        </div>
                      )}
                    </div>

                    <Form.Check
                      className='mb-2'
                      id='carrierImportCompareCreated'
                      label={intl.formatMessage(M.compareCreated)}
                      checked={matchingRules.comparePolicyCreated}
                      disabled={isWorking}
                      onChange={(event) =>
                        setMatchingRules((rules) => ({
                          ...rules,
                          comparePolicyCreated: event.target.checked,
                        }))
                      }
                    />
                    {matchingRules.comparePolicyCreated && (
                      <div className='row g-3'>
                        <div className='col-md-8'>
                          <Form.Group>
                            <Form.Label>{intl.formatMessage(M.sourceDate)}</Form.Label>
                            <Form.Select
                              value={matchingRules.createdDateSource}
                              disabled={isWorking}
                              onChange={(event) =>
                                setMatchingRules((rules) => ({
                                  ...rules,
                                  createdDateSource: event.target
                                    .value as MatchingRules['createdDateSource'],
                                }))
                              }
                            >
                              <option value='RECEIVED_DATE'>
                                {intl.formatMessage(M.receivedDate)}
                              </option>
                              <option value='ISSUED_DATE'>
                                {intl.formatMessage(M.issuedDate)}
                              </option>
                              <option value='EFFECTIVE_DATE'>
                                {intl.formatMessage(M.effectiveDate)}
                              </option>
                            </Form.Select>
                          </Form.Group>
                        </div>
                        <div className='col-md-4'>
                          <Form.Group>
                            <Form.Label>{intl.formatMessage(M.toleranceDays)}</Form.Label>
                            <Form.Control
                              type='number'
                              min={0}
                              max={366}
                              value={matchingRules.createdDateToleranceDays}
                              disabled={isWorking}
                              onChange={(event) =>
                                setMatchingRules((rules) => ({
                                  ...rules,
                                  createdDateToleranceDays: Number(event.target.value),
                                }))
                              }
                            />
                          </Form.Group>
                        </div>
                      </div>
                    )}

                    {!customRulesValid && (
                      <Alert variant='warning' className='py-2 mt-3 mb-0'>
                        {intl.formatMessage(M.invalidMatchingRules)}
                      </Alert>
                    )}
                  </>
                )}
              </div>

              <div className='border rounded p-3 mb-3'>
                <div className='d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2'>
                  <div>
                    <Form.Label className='fw-bold mb-1'>
                      {intl.formatMessage(M.importScope)}
                    </Form.Label>
                    <div className='text-muted'>
                      {intl.formatMessage(M.scopeSummary, {
                        carriers: selectedTargetCarriers.length,
                        types: selectedPolicyTypes.length,
                        dates: dateScopeLabel,
                      })}
                    </div>
                  </div>
                  <Form.Check
                    type='switch'
                    id='limitPolicyDates'
                    label={intl.formatMessage(M.limitPolicyDates)}
                    checked={limitPolicyDates}
                    disabled={isWorking}
                    onChange={(event) => setLimitPolicyDates(event.target.checked)}
                  />
                </div>

                {limitPolicyDates ? (
                  <>
                    <Alert variant='warning' className='py-2 mb-3'>
                      {intl.formatMessage(M.dateFilterActiveWarning)}
                    </Alert>
                    <div className='row g-3 mb-1'>
                      <div className='col-md-4'>
                        <DateInput
                          id='policyDateFrom'
                          name='policyDateFrom'
                          label={intl.formatMessage(M.policyDateFrom)}
                          value={policyDateFrom || null}
                          disabled={isWorking}
                          onChange={(_field, value) => setPolicyDateFrom(value ?? '')}
                        />
                      </div>
                      <div className='col-md-4'>
                        <DateInput
                          id='policyDateTo'
                          name='policyDateTo'
                          label={intl.formatMessage(M.policyDateTo)}
                          value={policyDateTo || null}
                          disabled={isWorking}
                          onChange={(_field, value) => setPolicyDateTo(value ?? '')}
                        />
                      </div>
                      <div className='col-md-4'>
                        <Form.Group>
                          <Form.Label>{intl.formatMessage(M.dateCriteria)}</Form.Label>
                          <Form.Select
                            value={dateCriteria}
                            disabled={isWorking}
                            onChange={(event) =>
                              setDateCriteria(event.target.value as 'submitted' | 'effective')
                            }
                          >
                            <option value='effective'>
                              {intl.formatMessage(M.dateCriteriaEffective)}
                            </option>
                            <option value='submitted'>
                              {intl.formatMessage(M.dateCriteriaSubmitted)}
                            </option>
                          </Form.Select>
                        </Form.Group>
                      </div>
                    </div>
                    <Form.Text className='text-muted d-block'>
                      {intl.formatMessage(M.policyDateRangeHint)}
                    </Form.Text>
                  </>
                ) : (
                  <Alert variant='success' className='py-2 mb-0'>
                    {intl.formatMessage(M.dateFilterDisabledHint)}
                  </Alert>
                )}
              </div>

              <Form.Group className='mb-3'>
                <DateInput
                  id='asOfDate'
                  name='asOfDate'
                  label={intl.formatMessage(M.asOfDate)}
                  value={asOfDate || null}
                  disabled={isWorking}
                  onChange={(_field, value) => setAsOfDate(value ?? '')}
                />
                <Form.Text className='text-muted'>{intl.formatMessage(M.asOfDateHint)}</Form.Text>
              </Form.Group>

              <Form.Group className='mb-3'>
                <Form.Label>{intl.formatMessage(M.fileCsv)}</Form.Label>
                <Form.Control
                  type='file'
                  accept='.csv,.xlsx,.xls'
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setFile(e.target.files?.[0] || null)
                  }}
                  disabled={isWorking}
                />
              </Form.Group>

              {detectedFields.length > 0 && (
                <div className='mb-3'>
                  <small className='text-muted me-2'>
                    {intl.formatMessage(M.previewAvailable)}
                  </small>
                  {detectedFields.map((field) => (
                    <Badge bg='secondary' className='me-1' key={field}>
                      {field.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              )}

              {previewResult && (
                <div className='border rounded p-3 mb-3'>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <h5 className='mb-0'>{intl.formatMessage(M.previewTitle)}</h5>
                    <small className='text-muted'>{intl.formatMessage(M.previewNoWrites)}</small>
                  </div>
                  <div className='d-flex flex-wrap gap-2 mb-3'>
                    <Badge bg='primary' text='light'>
                      {intl.formatMessage(M.statProcessed)} {previewResult.processedCount}
                    </Badge>
                    <Badge bg='success'>
                      {intl.formatMessage(M.statMatched)} {previewResult.matchedCount}
                    </Badge>
                    <Badge bg='warning'>
                      {intl.formatMessage(M.statAmbiguous)} {previewResult.ambiguousCount}
                    </Badge>
                    <Badge bg='secondary'>
                      {intl.formatMessage(M.statUnmatched)} {previewResult.unmatchedCount}
                    </Badge>
                    <Badge bg='danger'>
                      {intl.formatMessage(M.statErrors)} {previewResult.errorsCount}
                    </Badge>
                  </div>
                  {previewResult.unavailableSelectedFields.length > 0 && (
                    <Alert variant='danger' className='py-2'>
                      {intl.formatMessage(M.previewUnavailable, {
                        fields: previewResult.unavailableSelectedFields.join(', '),
                      })}
                    </Alert>
                  )}
                  {limitPolicyDates && previewResult.dateScopeExcludedCount > 0 && (
                    <Alert
                      variant={
                        previewResult.dateScopeExcludedCount / previewResult.processedCount >= 0.25
                          ? 'danger'
                          : 'warning'
                      }
                      className='py-2 d-flex flex-wrap justify-content-between align-items-center gap-2'
                    >
                      <span>
                        {intl.formatMessage(M.dateScopeExcludedWarning, {
                          count: previewResult.dateScopeExcludedCount,
                        })}
                      </span>
                      <Button
                        variant='outline-dark'
                        size='sm'
                        onClick={() => setLimitPolicyDates(false)}
                      >
                        {intl.formatMessage(M.searchAllDates)}
                      </Button>
                    </Alert>
                  )}
                  {previewResult.rowsTruncated && (
                    <Alert variant='info' className='py-2'>
                      {intl.formatMessage(M.previewTruncated)}
                    </Alert>
                  )}
                  <div style={{maxHeight: '320px', overflow: 'auto'}}>
                    <Table striped bordered hover size='sm' responsive className='mb-0'>
                      <thead>
                        <tr>
                          <th>{intl.formatMessage(M.thRow)}</th>
                          <th>{intl.formatMessage(M.outcome)}</th>
                          <th>{intl.formatMessage(M.thMemberId)}</th>
                          <th>{intl.formatMessage(M.thName)}</th>
                          <th>{intl.formatMessage(M.policyType)}</th>
                          <th>{intl.formatMessage(M.thReason)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewResult.previewRows.map((row) => (
                          <tr key={`${row.rowNumber}-${row.memberId || ''}`}>
                            <td>{row.rowNumber}</td>
                            <td>
                              <Badge
                                bg={
                                  row.outcome === 'MATCHED'
                                    ? 'success'
                                    : row.outcome === 'AMBIGUOUS'
                                    ? 'warning'
                                    : row.outcome === 'ERROR'
                                    ? 'danger'
                                    : 'secondary'
                                }
                              >
                                {row.outcome}
                              </Badge>
                            </td>
                            <td>{row.memberId || dash}</td>
                            <td>{row.name || dash}</td>
                            <td>{row.policyType || dash}</td>
                            <td>
                              <div>{row.reason}</div>
                              {(row.outcome === 'MATCHED' || row.outcome === 'AMBIGUOUS') &&
                                row.candidates.length > 0 && (
                                  <Button
                                    variant='link'
                                    size='sm'
                                    className='p-0 mt-1'
                                    onClick={() => setMatchDetail(row)}
                                  >
                                    {intl.formatMessage(M.viewDetails, {
                                      count: row.candidateCount || row.candidates.length,
                                    })}
                                  </Button>
                                )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Progress bar — only visible during async processing */}
              {asyncProgress !== null && (
                <div className='mb-3'>
                  <div className='d-flex justify-content-between mb-1'>
                    <small className='text-muted'>{asyncProgress.msg}</small>
                    <small className='text-muted fw-bold'>
                      {asyncProgress.pct}
                      {percentSign}
                    </small>
                  </div>
                  <ProgressBar
                    animated
                    now={asyncProgress.pct}
                    variant='primary'
                    style={{height: '10px'}}
                  />
                </div>
              )}

              {fetchingUnmatched && (
                <div className='text-center text-muted mb-2'>
                  <Spinner animation='border' size='sm' className='me-1' />
                  {intl.formatMessage(M.loadingUnmatched)}
                </div>
              )}

              {syncError && (
                <Alert variant='danger'>
                  {intl.formatMessage(
                    {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
                    {
                      message:
                        typeof syncError === 'string'
                          ? syncError
                          : intl.formatMessage(M.uploadFailed),
                    }
                  )}
                </Alert>
              )}
            </>
          ) : (
            <>
              <Alert variant={importResult.success ? 'success' : 'danger'}>
                {importResult.message}
              </Alert>

              <div className='d-flex gap-3 mb-3'>
                <div>
                  <strong>{intl.formatMessage(M.statProcessed)}</strong>
                  {space}
                  <Badge bg='primary' text='light'>
                    {importResult.processedCount}
                  </Badge>
                </div>
                <div>
                  <strong>{intl.formatMessage(M.statMatched)}</strong>
                  {space}
                  <Badge bg='success'>{importResult.matchedCount}</Badge>
                </div>
                <div>
                  <strong>{intl.formatMessage(M.statUnmatched)}</strong>
                  {space}
                  <Badge bg='warning' text='dark'>
                    {importResult.unmatchedCount}
                  </Badge>
                </div>
                <div>
                  <strong>{intl.formatMessage(M.statErrors)}</strong>
                  {space}
                  <Badge bg='danger'>{importResult.errorsCount}</Badge>
                </div>
              </div>

              {importResult.unmatchedRows && importResult.unmatchedRows.length > 0 && (
                <>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <h6 className='mb-0'>
                      {intl.formatMessage(M.unmatchedRows, {
                        count: searchQuery.trim()
                          ? intl.formatMessage(M.ofTotal, {
                              shown: totalFiltered,
                              total: importResult.unmatchedRows.length,
                            })
                          : totalFiltered,
                      })}
                    </h6>
                    <Button
                      variant='outline-primary'
                      size='sm'
                      onClick={() =>
                        exportUnmatchedCsv(importResult.unmatchedRows, statusTitleByCode)
                      }
                    >
                      {intl.formatMessage(M.exportCsv)}
                    </Button>
                  </div>

                  <Form.Control
                    type='text'
                    placeholder={intl.formatMessage(M.phSearch)}
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className='mb-2'
                    size='sm'
                  />

                  <Table size='sm' striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>{intl.formatMessage(M.thRow)}</th>
                        <th>{intl.formatMessage(M.thMemberId)}</th>
                        <th>{intl.formatMessage(M.thName)}</th>
                        <th>{intl.formatMessage(M.thCarrierStatus)}</th>
                        <th>{intl.formatMessage(M.thSystemStatus)}</th>
                        <th>{intl.formatMessage(M.thAgent)}</th>
                        <th>{intl.formatMessage(M.thReason)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRows.map((row, idx) => (
                        <tr key={idx}>
                          <td>{row.rowNumber}</td>
                          <td>{row.memberId || dash}</td>
                          <td>{row.name || dash}</td>
                          <td>{row.carrierStatusRaw || row.carrierStatusGeneric || dash}</td>
                          <td>
                            {row.systemPolicyStatus && statusTitleByCode[row.systemPolicyStatus] ? (
                              <BadgeStatePolicy
                                status={statusTitleByCode[row.systemPolicyStatus]}
                                color={statusColorByCode[row.systemPolicyStatus] || 'secondary'}
                              />
                            ) : (
                              dash
                            )}
                          </td>
                          <td>
                            {row.systemAgentName || row.systemAgentEmail ? (
                              <>
                                {row.systemAgentName || dash}
                                {row.systemAgentEmail ? ` (${row.systemAgentEmail})` : ''}
                              </>
                            ) : (
                              dash
                            )}
                          </td>
                          <td>{row.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {totalPages > 1 && (
                    <div className='d-flex justify-content-between align-items-center'>
                      <small className='text-muted'>
                        {intl.formatMessage(M.pageOf, {page: safePage, total: totalPages})}
                      </small>
                      <Pagination size='sm' className='mb-0'>
                        <Pagination.First
                          disabled={safePage === 1}
                          onClick={() => setCurrentPage(1)}
                        />
                        <Pagination.Prev
                          disabled={safePage === 1}
                          onClick={() => setCurrentPage(safePage - 1)}
                        />
                        {paginationItems}
                        <Pagination.Next
                          disabled={safePage === totalPages}
                          onClick={() => setCurrentPage(safePage + 1)}
                        />
                        <Pagination.Last
                          disabled={safePage === totalPages}
                          onClick={() => setCurrentPage(totalPages)}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleClose} disabled={isPolling}>
            {importResult ? intl.formatMessage(M.close) : intl.formatMessage(M.cancel)}
          </Button>
          {!importResult && (
            <>
              <Button
                variant='outline-primary'
                onClick={handlePreview}
                disabled={
                  isWorking ||
                  !carrierId ||
                  targetCarrierIds.length === 0 ||
                  policyTypeIds.length === 0 ||
                  (limitPolicyDates && (!policyDateFrom || !policyDateTo)) ||
                  !asOfDate ||
                  !file ||
                  !customRulesValid
                }
              >
                {isPreviewing ? (
                  <>
                    <Spinner animation='border' size='sm' className='me-1' />{' '}
                    {intl.formatMessage(M.processing)}
                  </>
                ) : (
                  intl.formatMessage(M.preview)
                )}
              </Button>
              <Button
                variant='primary'
                onClick={handleUpload}
                disabled={
                  isWorking ||
                  !previewResult?.success ||
                  previewResult.unavailableSelectedFields.length > 0
                }
              >
                {isAsyncUploading ? (
                  <>
                    <Spinner animation='border' size='sm' className='me-1' />{' '}
                    {intl.formatMessage(M.btnUploading)}
                  </>
                ) : isSyncUploading ? (
                  <>
                    <Spinner animation='border' size='sm' className='me-1' />{' '}
                    {intl.formatMessage(M.processing)}
                  </>
                ) : isPolling || fetchingUnmatched ? (
                  <>
                    <Spinner animation='border' size='sm' className='me-1' />{' '}
                    {intl.formatMessage(M.btnWorking)}
                  </>
                ) : (
                  intl.formatMessage(M.applyImport)
                )}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(matchDetail)} onHide={() => setMatchDetail(null)} size='xl' centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {intl.formatMessage(
              matchDetail?.outcome === 'MATCHED' ? M.matchedPolicyTitle : M.candidatesTitle
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {matchDetail && (
            <>
              <Alert
                variant={matchDetail.outcome === 'MATCHED' ? 'success' : 'warning'}
                className='py-2'
              >
                {matchDetail.outcome === 'MATCHED'
                  ? intl.formatMessage(M.matchedPolicyDescription)
                  : intl.formatMessage(M.candidatesDescription, {
                      count: matchDetail.candidateCount || matchDetail.candidates.length,
                    })}
              </Alert>
              {matchDetail.candidatesTruncated && (
                <Alert variant='info' className='py-2'>
                  {intl.formatMessage(M.candidatesTruncated, {
                    shown: matchDetail.candidates.length,
                    total: matchDetail.candidateCount,
                  })}
                </Alert>
              )}
              <Table responsive hover size='sm' className='align-middle mb-0'>
                <thead>
                  <tr>
                    <th>{intl.formatMessage(M.candidatePolicy)}</th>
                    <th>{intl.formatMessage(M.thMemberId)}</th>
                    <th>{intl.formatMessage(M.candidateCarrier)}</th>
                    <th>{intl.formatMessage(M.thSystemStatus)}</th>
                    <th>{intl.formatMessage(M.candidateEffective)}</th>
                    <th>{intl.formatMessage(M.candidateSubmitted)}</th>
                    <th>{intl.formatMessage(M.candidateAgent)}</th>
                  </tr>
                </thead>
                <tbody>
                  {matchDetail.candidates.map((candidate) => (
                    <tr key={candidate.policyId}>
                      <td>
                        <a
                          className='text-primary fw-bold'
                          href={`/one-policy/${candidate.policyId}`}
                          target='_blank'
                          rel='noreferrer'
                        >
                          {candidate.policyName || intl.formatMessage(M.viewPolicy)}
                        </a>
                      </td>
                      <td>{candidate.memberId || dash}</td>
                      <td>
                        <div>{candidate.carrier || dash}</div>
                        <small className='text-muted'>{candidate.policyType || dash}</small>
                      </td>
                      <td>
                        {candidate.status && statusTitleByCode[candidate.status] ? (
                          <BadgeStatePolicy
                            status={statusTitleByCode[candidate.status]}
                            color={statusColorByCode[candidate.status] || 'secondary'}
                          />
                        ) : (
                          candidate.status || dash
                        )}
                      </td>
                      <td>{candidate.effectiveDate || dash}</td>
                      <td>{candidate.submittedDate || dash}</td>
                      <td>
                        <div>{candidate.agentName || dash}</div>
                        {candidate.agentEmail && (
                          <small className='text-muted'>{candidate.agentEmail}</small>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setMatchDetail(null)}>
            {intl.formatMessage(M.close)}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
